import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
    Sparkles,
    Calculator,
    Users,
    CalendarCheck,
    CreditCard,
    ArrowRight,
    Check,
    ExternalLink,
} from "lucide-react";

/**
 * WorkflowRunner — chained purchase experience.
 *
 * Steps are built from the deal's workflow config in a fixed order:
 *   splash → calculator → select_members → book → checkout
 *
 * A calculator-enabled deal is quote-only, though: the vendor follows up
 * directly with a firm price rather than the member paying Perx, so
 * `select_members` and `checkout` are dropped from the sequence and it ends
 * instead at `done` (splash → calculator → book → done). See `quoteOnly`
 * below.
 *
 * Only ticked steps render. The runner is a single dialog; it advances
 * the visible step as the member confirms each one. `initialStep` lets a
 * caller reopen it partway through — used when the calculator step sends
 * the member to the separate `/calculator` page (breaking React state) and
 * they come back needing to resume rather than restart.
 */
export default function WorkflowRunner({
    open,
    onClose,
    deal,
    user,
    onCheckout, // (extraPayload) => Promise — parent triggers Stripe
    initialStep, // optional — resume past a step already completed outside the dialog (e.g. the calculator page)
}) {
    // Keep the workflow object stable when the deal has no workflow config.
    const wf = useMemo(() => deal?.workflow || {}, [deal?.workflow]);
    const navigate = useNavigate();

    // A calculator-enabled deal is treated as quote-only: the vendor follows
    // up directly with a firm price, so the workflow never reaches Stripe
    // checkout, and the separate "select members" step is dropped — the
    // calculator's own household-member list already answers "who is this
    // for" in one combined quote, so asking again here would be redundant.
    const quoteOnly = Boolean(
        wf.calculator_enabled || wf.hk_property_insurance_enabled || wf.helper_calculator_enabled
    );

    const steps = useMemo(() => {
        const s = [];
        if (wf.splash_enabled) s.push("splash");
        if (wf.calculator_enabled) s.push("calculator");
        if (wf.helper_calculator_enabled) s.push("helper_calculator");
        if (wf.hk_property_insurance_enabled) s.push("hk_property_insurance");
        if (!quoteOnly && wf.select_members_enabled) s.push("select_members");
        if (wf.booking_enabled) s.push("book");
        s.push(quoteOnly ? "done" : "checkout");
        return s;
    }, [wf, quoteOnly]);

    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (!open) return;
        if (initialStep) {
            const i = steps.indexOf(initialStep);
            setIdx(i >= 0 ? i : steps.length - 1);
        } else {
            setIdx(0);
        }
    }, [open, deal?.id, initialStep, steps]);

    const [subMembers, setSubMembers] = useState([]);
    const [selectedSubs, setSelectedSubs] = useState([]);
    const [bookingConfirmed, setBookingConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    // Guards persist effect from firing with the initial [] before the async
    // hydrate has computed the true initial selection (was a race — see
    // iteration_8 test report).
    const [hydrated, setHydrated] = useState(false);
    const terminalEffectKey = useRef(null);

    // Per-product selection memory. We namespace by deal id so members with
    // multiple insurance products don't leak selections between them.
    const storageKey = deal?.id ? `perx_wf_subs_${deal.id}` : null;

    useEffect(() => {
        setHydrated(false);
    }, [open, deal?.id]);

    useEffect(() => {
        if (!open || !user || quoteOnly) return;
        api.get("/member/sub-users")
            .then((r) => {
                const list = r.data || [];
                setSubMembers(list);

                // Prefill selection: (1) previously-saved choice for THIS
                // product wins, (2) otherwise auto-select every secondary
                // member so the primary doesn't have to tick each one on
                // first purchase.
                let initial = null;
                if (storageKey) {
                    try {
                        const raw = localStorage.getItem(storageKey);
                        if (raw) {
                            const parsed = JSON.parse(raw);
                            if (Array.isArray(parsed)) {
                                // Drop any ids that no longer exist (member removed)
                                const validIds = new Set(list.map((s) => s.id));
                                initial = parsed.filter((id) => validIds.has(id));
                            }
                        }
                    } catch {
                        initial = null;
                    }
                }
                if (initial == null) initial = list.map((s) => s.id);
                setSelectedSubs(initial);
                setHydrated(true);
            })
            .catch(() => {
                setSubMembers([]);
                setHydrated(true);
            });
    }, [open, user, storageKey, quoteOnly]);

    // Persist changes as the primary member ticks/unticks. Guarded by
    // `hydrated` so we don't clobber saved state with the initial [].
    useEffect(() => {
        if (!open || !storageKey || !hydrated) return;
        try {
            localStorage.setItem(storageKey, JSON.stringify(selectedSubs));
        } catch {
            /* quota exceeded or private mode — silently skip */
        }
    }, [selectedSubs, storageKey, open, hydrated]);

    const current = steps[idx];
    const advance = () => setIdx((i) => Math.min(i + 1, steps.length - 1));

    // Auto-fire the moment we reach the last step — either real Stripe
    // checkout, or (quote-only deals) the in-dialog confirmation screen.
    useEffect(() => {
        if (!open || (current !== "checkout" && current !== "done")) {
            terminalEffectKey.current = null;
            return;
        }

        // Prevent duplicate case creation / checkout if a callback or selection
        // reference changes while the terminal step is still displayed.
        const effectKey = `${deal?.id || "no-deal"}:${current}`;
        if (terminalEffectKey.current === effectKey) return;
        terminalEffectKey.current = effectKey;

        (async () => {
            setBusy(true);
            try {
                // If sub_user_ids selected AND kind=deal, raise one vendor case per
                // ticked member (option B from the requirements) BEFORE checkout
                // so the vendor sees each life. Not reachable for quote-only
                // deals — select_members isn't part of their step sequence.
                if (
                    !quoteOnly &&
                    wf.select_members_enabled &&
                    selectedSubs.length > 0 &&
                    deal.payment_mode !== "referral"
                ) {
                    await Promise.all(
                        selectedSubs.map((subId) =>
                            api.post("/member/vendor-cases", {
                                deal_id: deal.id,
                                case_type: "member_line_item",
                                sub_user_ids: [subId],
                            })
                        )
                    );
                }
                if (bookingConfirmed) {
                    await api.post("/member/vendor-cases", {
                        deal_id: deal.id,
                        case_type: "booking",
                        booking_source: wf.booking_source_url,
                    });
                }
                if (current === "checkout") {
                    onCheckout?.({ include_sub_user_ids: selectedSubs });
                    onClose?.();
                }
                // current === "done": nothing left to fire — just stop
                // spinning so the confirmation screen renders below.
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setBusy(false);
            }
        })();
    }, [
        current,
        open,
        quoteOnly,
        wf.select_members_enabled,
        selectedSubs,
        deal?.payment_mode,
        deal?.id,
        bookingConfirmed,
        wf.booking_source_url,
        onCheckout,
        onClose,
    ]);

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="wf-runner"
                className="max-w-3xl bg-white border-navy-100 max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700 flex items-center gap-2">
                        {STEP_ICONS[current] || <Sparkles size={18} />}{" "}
                        {STEP_TITLES[current]}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Complete each step to finish your purchase of {deal?.title || "this deal"}.
                    </DialogDescription>
                    <StepDots current={current} steps={steps} />
                </DialogHeader>

                {current === "splash" && (
                    <SplashStep wf={wf} onContinue={advance} />
                )}
                {current === "calculator" && (
                    <CalculatorStep
                        deal={deal}
                        wf={wf}
                        onContinue={() => {
                            navigate(
                                (wf.calculator_source_url &&
                                    /^https?:\/\//i.test(wf.calculator_source_url) &&
                                    wf.calculator_source_url) ||
                                `/calculator?deal=${deal.id}`
                            );
                            onClose?.();
                        }}
                        onSkip={advance}
                    />
                )}
                {current === "helper_calculator" && (
                    <HelperCalculatorStep
                        wf={wf}
                        onContinue={() => {
                            const url = wf.helper_calculator_source_url;
                            if (url && /^https?:\/\//i.test(url)) {
                                window.open(url, "_blank", "noopener");
                                advance();
                                return;
                            }
                            navigate(`/member/helper-insurance?deal=${deal.id}`);
                            onClose?.();
                        }}
                        onSkip={advance}
                    />
                )}
                {current === "hk_property_insurance" && (
                    <HkPropertyInsuranceStep
                        wf={wf}
                        onContinue={() => {
                            const url = wf.hk_property_insurance_source_url;
                            if (url && /^https?:\/\//i.test(url)) {
                                window.open(url, "_blank", "noopener");
                                advance();
                                return;
                            }
                            navigate(`/member/property-insurance?deal=${deal.id}`);
                            onClose?.();
                        }}
                        onSkip={advance}
                    />
                )}
                {current === "select_members" && (
                    <SelectMembersStep
                        subMembers={subMembers}
                        selected={selectedSubs}
                        setSelected={setSelectedSubs}
                        onContinue={advance}
                    />
                )}
                {current === "book" && (
                    <BookingStep
                        wf={wf}
                        confirmed={bookingConfirmed}
                        setConfirmed={setBookingConfirmed}
                        onContinue={advance}
                    />
                )}
                {current === "checkout" && (
                    <div className="text-center py-12">
                        <div className="text-navy-500 text-sm">
                            {busy ? "Redirecting to secure Stripe checkout…" : "Ready to pay"}
                        </div>
                    </div>
                )}
                {current === "done" && (
                    <div className="text-center py-12 space-y-4">
                        {busy ? (
                            <div className="text-navy-500 text-sm">Finishing up…</div>
                        ) : (
                            <>
                                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                                    <Check size={28} className="text-emerald-600" />
                                </div>
                                <div className="text-navy-700 font-medium">
                                    Sent to the vendor
                                </div>
                                <p className="text-navy-500 text-sm max-w-sm mx-auto">
                                    {deal?.vendor || "The vendor"} will follow up directly
                                    with your firm quote.
                                </p>
                                <Button
                                    onClick={onClose}
                                    data-testid="wf-done-close"
                                    className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-8"
                                >
                                    Close
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

const STEP_TITLES = {
    splash: "Before you continue",
    calculator: "Get an indicative quote",
    helper_calculator: "Get a HK helper insurance quote",
    hk_property_insurance: "Get HK property insurance",
    select_members: "Who is this for?",
    book: "Book a time with the vendor",
    checkout: "Payment",
    done: "All set",
};
const STEP_ICONS = {
    splash: <Sparkles size={18} className="text-gold-600" />,
    calculator: <Calculator size={18} className="text-gold-600" />,
    helper_calculator: <Calculator size={18} className="text-gold-600" />,
    hk_property_insurance: <Calculator size={18} className="text-gold-600" />,
    select_members: <Users size={18} className="text-gold-600" />,
    book: <CalendarCheck size={18} className="text-gold-600" />,
    checkout: <CreditCard size={18} className="text-gold-600" />,
    done: <Check size={18} className="text-emerald-600" />,
};

function StepDots({ current, steps }) {
    return (
        <div className="flex gap-1.5 pt-2" data-testid="wf-dots">
            {steps.map((s) => (
                <span
                    key={s}
                    className={`h-1.5 rounded-full transition-all ${s === current ? "bg-gold-500 w-8" : "bg-navy-100 w-3"
                        }`}
                />
            ))}
        </div>
    );
}

function SplashStep({ wf, onContinue }) {
    if (wf.splash_source_url && /^https?:\/\//i.test(wf.splash_source_url)) {
        return (
            <div className="space-y-4">
                <iframe
                    src={wf.splash_source_url}
                    title="Splash"
                    data-testid="wf-splash-iframe"
                    className="w-full h-[420px] rounded-xl border border-navy-100"
                />
                <Button
                    onClick={onContinue}
                    data-testid="wf-splash-continue"
                    className="rounded-full w-full bg-navy-700 hover:bg-navy-800 text-white h-11"
                >
                    Continue <ArrowRight size={14} className="ml-2" />
                </Button>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            {wf.splash_image_url && (
                <img
                    src={wf.splash_image_url}
                    alt=""
                    className="w-full h-56 object-cover rounded-xl"
                    data-testid="wf-splash-img"
                />
            )}
            <div className="text-navy-500 text-sm leading-relaxed">
                {wf.splash_body || "Ready when you are — pandas approve."}
            </div>
            <Button
                onClick={onContinue}
                data-testid="wf-splash-continue"
                className="rounded-full w-full bg-navy-700 hover:bg-navy-800 text-white h-11"
            >
                Continue <ArrowRight size={14} className="ml-2" />
            </Button>
        </div>
    );
}

function CalculatorStep({ wf, onContinue, onSkip }) {
    const external =
        wf.calculator_source_url &&
        /^https?:\/\//i.test(wf.calculator_source_url);
    return (
        <div className="space-y-4">
            <p className="text-navy-500 text-sm">
                Get an indicative quote before you pay.{" "}
                {external
                    ? "We'll open the vendor's quote tool in a new tab."
                    : "You'll be taken to the Perx premium calculator."}
            </p>
            <div className="flex gap-2">
                <Button
                    onClick={onContinue}
                    data-testid="wf-calc-open"
                    className="rounded-full flex-1 bg-navy-700 hover:bg-navy-800 text-white h-11"
                >
                    {external ? "Open calculator" : "Open Perx calculator"}
                    {external ? (
                        <ExternalLink size={14} className="ml-2" />
                    ) : (
                        <ArrowRight size={14} className="ml-2" />
                    )}
                </Button>
                <Button
                    onClick={onSkip}
                    variant="outline"
                    data-testid="wf-calc-skip"
                    className="rounded-full h-11 border-navy-200"
                >
                    Skip
                </Button>
            </div>
        </div>
    );
}

function HelperCalculatorStep({ wf, onContinue, onSkip }) {
    const external =
        wf.helper_calculator_source_url &&
        /^https?:\/\//i.test(wf.helper_calculator_source_url);
    return (
        <div className="space-y-4">
            <p className="text-navy-500 text-sm">
                Get your HK helper insurance price.{" "}
                {external
                    ? "We'll open the vendor's quote tool in a new tab."
                    : "Choose the plan term and your helper's age to see the Perx price, then apply."}
            </p>
            <div className="flex gap-2">
                <Button
                    onClick={onContinue}
                    data-testid="wf-helper-open"
                    className="rounded-full flex-1 bg-navy-700 hover:bg-navy-800 text-white h-11"
                >
                    {external ? "Open quote tool" : "Get my price"}
                    {external ? (
                        <ExternalLink size={14} className="ml-2" />
                    ) : (
                        <ArrowRight size={14} className="ml-2" />
                    )}
                </Button>
                <Button
                    onClick={onSkip}
                    variant="outline"
                    data-testid="wf-helper-skip"
                    className="rounded-full h-11 border-navy-200"
                >
                    Skip
                </Button>
            </div>
        </div>
    );
}

function HkPropertyInsuranceStep({ wf, onContinue, onSkip }) {
    const external =
        wf.hk_property_insurance_source_url &&
        /^https?:\/\//i.test(wf.hk_property_insurance_source_url);

    return (
        <div className="space-y-4">
            <p className="text-navy-500 text-sm">
                Get an indicative premium for your Hong Kong property. Select your
                insurance plan, building type and gross floor area, then submit your
                details. If the insurer requires a referral quote, the broker will
                follow up directly.
                {external
                    ? " We'll open the configured quote tool in a new tab."
                    : ""}
            </p>
            <div className="flex gap-2">
                <Button
                    onClick={onContinue}
                    data-testid="wf-hk-property-open"
                    className="rounded-full flex-1 bg-navy-700 hover:bg-navy-800 text-white h-11"
                >
                    {external ? "Open quote tool" : "Get property insurance quote"}
                    {external ? (
                        <ExternalLink size={14} className="ml-2" />
                    ) : (
                        <ArrowRight size={14} className="ml-2" />
                    )}
                </Button>
                <Button
                    onClick={onSkip}
                    variant="outline"
                    data-testid="wf-hk-property-skip"
                    className="rounded-full h-11 border-navy-200"
                >
                    Skip
                </Button>
            </div>
        </div>
    );
}

function SelectMembersStep({ subMembers, selected, setSelected, onContinue }) {
    const toggle = (id) =>
        setSelected((s) =>
            s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
        );
    const allSelected =
        subMembers.length > 0 && selected.length === subMembers.length;
    const toggleAll = () => {
        setSelected(allSelected ? [] : subMembers.map((s) => s.id));
    };
    return (
        <div className="space-y-4">
            <p className="text-navy-500 text-sm">
                Tick the secondary members you&apos;re buying this product for. Each ticked
                member will get their own case with the vendor.
                {subMembers.length > 1 && (
                    <span className="block text-[11px] text-navy-400 mt-1">
                        We&apos;ve prefilled your last selection for this product.
                    </span>
                )}
            </p>
            {subMembers.length === 0 ? (
                <div className="rounded-xl bg-cream border border-gold-200/60 p-4 text-navy-500 text-sm">
                    You haven&apos;t added any secondary members yet. This purchase will
                    cover only you.
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="text-navy-500">
                            {selected.length} of {subMembers.length} selected
                        </span>
                        <button
                            type="button"
                            onClick={toggleAll}
                            data-testid="wf-selmem-toggle-all"
                            className="text-navy-700 hover:text-gold-600 font-semibold underline underline-offset-2"
                        >
                            {allSelected ? "Deselect all" : "Select all"}
                        </button>
                    </div>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto rounded-xl border border-navy-100 p-3 bg-white">
                        {subMembers.map((s) => {
                            const checked = selected.includes(s.id);
                            return (
                                <label
                                    key={s.id}
                                    htmlFor={`wf-sub-${s.id}`}
                                    className={`flex items-center gap-3 rounded-lg p-2.5 cursor-pointer transition-colors ${checked ? "bg-gold-50" : "hover:bg-navy-50"
                                        }`}
                                >
                                    <Checkbox
                                        id={`wf-sub-${s.id}`}
                                        data-testid={`wf-sub-check-${s.id}`}
                                        checked={checked}
                                        onCheckedChange={() => toggle(s.id)}
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm text-navy-700 font-medium">
                                            {s.name}
                                        </div>
                                        <div className="text-[11px] text-navy-400">
                                            {s.relationship} · age {s.age ?? "—"}
                                        </div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </>
            )}
            <Button
                onClick={onContinue}
                data-testid="wf-selmem-continue"
                className="rounded-full w-full bg-navy-700 hover:bg-navy-800 text-white h-11"
            >
                Continue{" "}
                {selected.length > 0 ? `(${selected.length} selected)` : ""}
                <ArrowRight size={14} className="ml-2" />
            </Button>
        </div>
    );
}

function BookingStep({ wf, confirmed, setConfirmed, onContinue }) {
    const url = wf.booking_source_url;
    if (!url) {
        return (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                No TidyCal URL configured for this product. Skipping.
                <Button
                    onClick={onContinue}
                    className="mt-3 rounded-full bg-navy-700 text-white h-10 w-full"
                >
                    Continue
                </Button>
            </div>
        );
    }
    return (
        <div className="space-y-3">
            <p className="text-navy-500 text-sm">
                Pick a time that suits you. Once you&apos;ve booked with the vendor, tick
                the confirmation below.
            </p>
            <iframe
                src={url}
                title="TidyCal"
                data-testid="wf-book-iframe"
                className="w-full h-[520px] rounded-xl border border-navy-100 bg-white"
            />
            <label
                htmlFor="wf-book-confirmed"
                className="flex items-center gap-3 rounded-xl bg-cream border border-gold-200/60 p-3 cursor-pointer"
            >
                <Checkbox
                    id="wf-book-confirmed"
                    data-testid="wf-book-confirmed"
                    checked={confirmed}
                    onCheckedChange={(v) => setConfirmed(Boolean(v))}
                />
                <span className="text-sm text-navy-700">
                    I&apos;ve booked my slot in TidyCal above
                </span>
                {confirmed && (
                    <Check size={14} className="text-emerald-600 ml-auto" />
                )}
            </label>
            <Button
                onClick={onContinue}
                disabled={!confirmed}
                data-testid="wf-book-continue"
                className="rounded-full w-full bg-navy-700 hover:bg-navy-800 text-white h-11 disabled:opacity-50"
            >
                Continue to payment <ArrowRight size={14} className="ml-2" />
            </Button>
        </div>
    );
}
